import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { OpsCustomerSummary } from "../../shared/operations-schemas";
import type { SupportUnmatchedMail } from "../../shared/support-schemas";
import { ContentLoading } from "../components/ContentLoading";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { listCustomers } from "./api";
import type { OperationsNavigate } from "./navigation";
import { EmptyCard } from "./PageSection";
import { LabeledInput, LabeledSelect } from "./support-fields";
import {
  acceptUnmatchedMail,
  listUnmatchedMail,
  LIVE_LIST,
  rejectUnmatchedMail,
  supportKeys,
} from "./support-api";
import { formatWhen, REASON_LABELS } from "./support-format";
import { supportTicketPath } from "./support-route";

/**
 * Support mail that could not be safely matched to a customer (ADR-0089). It is not a ticket, and
 * AI does not read it, until someone accepts it for a customer.
 */
export function SupportUnmatched({
  canWrite,
  navigate,
}: {
  canWrite: boolean;
  navigate: OperationsNavigate;
}) {
  const mail = useQuery({
    queryKey: supportKeys.unmatched(),
    queryFn: listUnmatchedMail,
    ...LIVE_LIST,
  });
  const customers = useQuery({
    queryKey: ["ops", "support", "customer-choices"],
    queryFn: () => listCustomers({ page: 1, pageSize: 50 }),
    enabled: canWrite,
  });
  return (
    <section aria-labelledby="support-unmatched-title" className="flex flex-col gap-4">
      <div>
        <h2 id="support-unmatched-title" className="text-xl font-bold">
          Unmatched email
        </h2>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          Mail we could not safely match to a customer. It is not a ticket, and AI does not read it,
          until you accept it. Unaccepted mail is deleted after 30 days.
        </p>
      </div>
      {mail.isPending ? <ContentLoading label="Loading unmatched email…" variant="cards" /> : null}
      {mail.error ? <FieldError>{mail.error.message}</FieldError> : null}
      {mail.data?.mail.length === 0 ? <EmptyCard>No unmatched email.</EmptyCard> : null}
      {mail.data?.mail.map((item) => (
        <UnmatchedCard
          key={item.id}
          mail={item}
          canWrite={canWrite}
          customers={customers.data?.customers ?? []}
          navigate={navigate}
        />
      ))}
    </section>
  );
}

function UnmatchedCard({
  mail,
  canWrite,
  customers,
  navigate,
}: {
  mail: SupportUnmatchedMail;
  canWrite: boolean;
  customers: readonly OpsCustomerSummary[];
  navigate: OperationsNavigate;
}) {
  const sender = mail.fromAddress ?? mail.envelopeFrom;
  return (
    <article
      aria-label={mail.subject || "No subject"}
      className="flex flex-col gap-3 rounded-xl bg-card px-6 py-5 shadow-card"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-bold">{mail.subject || "No subject"}</h3>
          <p className="text-sm text-muted-foreground">
            {mail.fromName ? `${mail.fromName} <${sender}>` : sender} ·{" "}
            {formatWhen(mail.receivedAt)}
          </p>
        </div>
        <Badge variant="warning">{REASON_LABELS[mail.reason]}</Badge>
      </header>
      <p className="max-h-48 overflow-y-auto rounded-md bg-muted px-4 py-3 whitespace-pre-wrap">
        {mail.bodyText || "This message has no text."}
      </p>
      <p className="text-sm text-muted-foreground">
        Sender check: {mail.authentication || "No authentication results"}
      </p>
      {canWrite ? (
        <UnmatchedDecision mail={mail} customers={customers} navigate={navigate} />
      ) : null}
    </article>
  );
}

/** What accepting sends, said before anyone presses the button. */
function AcceptNote({ reason }: { reason: SupportUnmatchedMail["reason"] }) {
  return (
    <p className="text-sm text-muted-foreground">
      {reason === "automatic_reply"
        ? "Accepting opens a ticket. Automatic mail never gets a confirmation."
        : "Accepting opens a ticket and emails the sender a confirmation with its number."}
    </p>
  );
}

function UnmatchedDecision({
  mail,
  customers,
  navigate,
}: {
  mail: SupportUnmatchedMail;
  customers: readonly OpsCustomerSummary[];
  navigate: OperationsNavigate;
}) {
  const queryClient = useQueryClient();
  const [organizationId, setOrganizationId] = useState(mail.suggestedOrganizationId ?? "");
  const [requesterName, setRequesterName] = useState(mail.fromName);
  const refresh = () => queryClient.invalidateQueries({ queryKey: supportKeys.all });
  const accept = useMutation({
    mutationFn: () =>
      acceptUnmatchedMail(mail.id, {
        organizationId,
        ...(requesterName.trim() ? { requesterName: requesterName.trim() } : {}),
      }),
    onSuccess: async (result) => {
      await refresh();
      navigate(supportTicketPath(result.ticketId));
    },
  });
  const reject = useMutation({
    mutationFn: () => rejectUnmatchedMail(mail.id),
    onSuccess: refresh,
  });
  const busy = accept.isPending || reject.isPending;
  const error = accept.error ?? reject.error;
  return (
    <div className="flex flex-col gap-3 border-t border-muted pt-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <LabeledSelect
          id={`unmatched-${mail.id}-customer`}
          label="Customer"
          value={organizationId}
          disabled={busy}
          onChange={setOrganizationId}
          options={[
            { value: "", label: "Choose a customer" },
            ...customers.map((customer) => ({ value: customer.id, label: customer.name })),
          ]}
        />
        <LabeledInput
          id={`unmatched-${mail.id}-name`}
          label="Requester name"
          value={requesterName}
          disabled={busy}
          onChange={setRequesterName}
        />
      </div>
      <AcceptNote reason={mail.reason} />
      {error ? <FieldError>{error.message}</FieldError> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={busy}
          onClick={() => {
            reject.mutate();
          }}
        >
          {reject.isPending ? "Rejecting…" : "Reject"}
        </Button>
        <Button
          type="button"
          disabled={busy || organizationId === ""}
          onClick={() => {
            accept.mutate();
          }}
        >
          {accept.isPending ? "Accepting…" : "Accept as ticket"}
        </Button>
      </div>
    </div>
  );
}
